import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const Portfolio = ({ portfolio }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const categories = ['All', 'Weddings', 'Portraits', 'Events', 'Commercial'];

  const filteredPortfolio = selectedCategory === 'All'
    ? portfolio
    : portfolio.filter(item => item.category === selectedCategory);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredPortfolio.length);
  }, [filteredPortfolio.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + filteredPortfolio.length) % filteredPortfolio.length);
  }, [filteredPortfolio.length]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, nextImage, prevImage]);

  if (!portfolio || portfolio.length === 0) {
    return null;
  }

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            Our Work
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            Featured <span className="italic">Portfolio</span>
          </h2>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 text-sm tracking-wider uppercase transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-[#d4af37] text-[#0a0a0a]'
                  : 'text-[#a3a3a3] hover:text-[#d4af37] border border-[#262626] hover:border-[#d4af37]'
              }`}
              data-testid={`filter-${category.toLowerCase()}`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid - Bento Style */}
        <motion.div 
          layout
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4"
        >
          <AnimatePresence mode="popLayout">
            {filteredPortfolio.map((item, index) => {
              // Create varied sizes for bento effect
              const isLarge = index % 5 === 0;
              const isMedium = index % 3 === 0 && !isLarge;
              
              return (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className={`relative overflow-hidden cursor-pointer group ${
                    isLarge ? 'col-span-2 row-span-2' : isMedium ? 'col-span-2' : ''
                  }`}
                  onClick={() => openLightbox(index)}
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <div className={`relative ${isLarge ? 'aspect-square' : isMedium ? 'aspect-video' : 'aspect-square'} overflow-hidden`}>
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-[#0a0a0a]/0 group-hover:bg-[#0a0a0a]/60 transition-all duration-300 flex items-center justify-center">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileHover={{ opacity: 1, scale: 1 }}
                        className="opacity-0 group-hover:opacity-100 transition-all duration-300 text-center"
                      >
                        <ZoomIn className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
                        <p className="text-[#e5e5e5] font-['Playfair_Display'] text-lg">{item.title}</p>
                        <p className="text-[#d4af37] text-xs tracking-widest uppercase mt-1">{item.category}</p>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && filteredPortfolio[currentImageIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0a0a0a]/95 z-50 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-[#e5e5e5] hover:text-[#d4af37] transition-colors z-10"
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 md:left-8 text-[#e5e5e5] hover:text-[#d4af37] transition-colors z-10 p-2"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 md:right-8 text-[#e5e5e5] hover:text-[#d4af37] transition-colors z-10 p-2"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Image */}
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-5xl max-h-[80vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filteredPortfolio[currentImageIndex].image}
                alt={filteredPortfolio[currentImageIndex].title}
                className="max-w-full max-h-[80vh] object-contain"
              />
              <div className="text-center mt-4">
                <p className="text-[#e5e5e5] font-['Playfair_Display'] text-xl">
                  {filteredPortfolio[currentImageIndex].title}
                </p>
                <p className="text-[#d4af37] text-sm tracking-widest uppercase mt-1">
                  {filteredPortfolio[currentImageIndex].category}
                </p>
                <p className="text-[#a3a3a3] text-sm mt-2">
                  {currentImageIndex + 1} / {filteredPortfolio.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
